import type { Scope } from 'types/permissions';
import { ACLCheck } from './Helpers';

/**
 * The scopes a user must hold — all of them — to reach the benchmark-request page (R-3, G-1).
 * `create:own_company_deals` is the API's own requirement for the create. `read:early_access` is a
 * client-only gate: the confirmation's `View request` targets `/deal/view/:deal_id`, which `App.tsx`
 * registers only inside the early-access branch, so a caller without it would submit and then land on
 * an unmatched route. The route guard in `App.tsx` and every link into the page check the same pair.
 */
export const REQUEST_PAGE_SCOPES: readonly Scope[] = ['create:own_company_deals', 'read:early_access'];

/**
 * Whether the caller holds every required scope. The house checks (`useScopes().aclCheck`, `ACLCheck`)
 * are any-of, so "all of" is one check per scope, as `App.tsx` does when it nests `hasEarlyReleaseRead`.
 * @param {(scopes: Scope[]) => boolean} aclCheck - The any-of check to run per scope.
 * @returns {boolean} True only when every scope in `REQUEST_PAGE_SCOPES` is held.
 */
export const isRequestPageAllowed = (aclCheck: (scopes: Scope[]) => boolean): boolean =>
  REQUEST_PAGE_SCOPES.every((scope) => aclCheck([scope]));

/**
 * `isRequestPageAllowed` run against `Helpers.ACLCheck` — the storage-backed check — for call sites
 * that have no `useScopes().aclCheck` to hand: the router in `App.tsx` and the `NavBar` menu, which
 * already read permissions this way. Components inside a `RecoilRoot` should prefer passing
 * `useScopes().aclCheck` to `isRequestPageAllowed` so the answer tracks scope state reactively.
 * Same calling convention as `isSimplifiedBasicNav` in `vantageBasic.ts`.
 * @returns {boolean} True only when the caller may open `/deal/request`.
 */
export const canRequestBenchmarkReport = (): boolean => isRequestPageAllowed(ACLCheck);
